// Updated Sidebar component with hybrid approach
import React, { useEffect, useState } from 'react';
import { sideNav } from '../assets/data/nav-items';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSidebar, setSidebarCollapsed, selectSidebarCollapsed } from '../features/ui/uiSlice';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';

const Sidebar = ({ onLogout, onCollapseChange }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const collapsed = useSelector(selectSidebarCollapsed);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Notify parent component when collapsed state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange]);

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        dispatch(setSidebarCollapsed(true));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  // Filter navigation items based on user role
  const filteredNavItems = sideNav.filter((item) => {
    return item.roles.includes(user?.role);
  });

  const handleCollapseToggle = () => {
    dispatch(toggleSidebar());
  };

  return (
    <div
      className={`bg-dark-400 border-r border-dark-300 fixed left-0 top-16 h-[calc(100vh-4rem)] z-40 transition-all duration-300 shadow-hard overflow-hidden ${
        collapsed ? 'w-20' : 'w-72'
      }`}
    >
      {/* Custom trigger button */}
      <div
        className={`absolute top-3 ${
          collapsed ? 'left-1/2 transform -translate-x-1/2' : 'right-6'
        } cursor-pointer text-neutral-400 hover:text-white text-lg hover:scale-110 transition-all duration-200 p-1 rounded-md hover:bg-dark-200 z-10`}
        onClick={handleCollapseToggle}
      >
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>

      {/* User profile section */}
      <div className={`${collapsed ? 'pt-12 px-2 pb-6' : 'pt-12 px-6 pb-6'} text-center border-b border-dark-300`}>
        <div className="relative group">
          {/* Avatar */}
          <div
            className={`mx-auto bg-neutral-600 border-2 border-neutral-500 rounded-full flex items-center justify-center text-white transition-all duration-200 ${
              collapsed ? 'w-10 h-10' : 'w-16 h-16 mb-3'
            }`}
          >
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <UserOutlined className={collapsed ? 'text-sm' : 'text-xl'} />
            )}
          </div>
          
          {/* Tooltip for collapsed state */}
          {collapsed && (
            <div className="absolute left-full ml-2 top-0 bg-dark-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
              {user?.name || 'User'}
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="mt-3">
            <h3 className="text-white text-base font-semibold mb-1 truncate">
              {user?.name || 'User'}
            </h3>
            <p className="text-neutral-400 text-xs capitalize tracking-wide truncate">
              {user?.role || 'Member'}
            </p>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <div className={`${collapsed ? 'px-2' : 'px-3'} py-4 h-[calc(100vh-320px)] overflow-y-auto overflow-x-hidden`}>
        <nav className="space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <div key={item.path} className="relative group">
                <Link
                  to={item.path}
                  className={`flex items-center ${collapsed ? 'justify-center px-2 py-3' : 'space-x-3 px-3 py-3'} rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-neutral-800 text-white shadow-soft border-l-2 border-neutral-300'
                      : 'text-neutral-300 hover:text-white hover:bg-neutral-700'
                  }`}
                  onMouseEnter={() => setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <span className="text-lg flex-shrink-0">
                    {item.icon}
                  </span>
                  {!collapsed && (
                    <span className="capitalize tracking-wide truncate">
                      {item.label}
                    </span>
                  )}
                </Link>
                
                {/* Tooltip for collapsed state */}
                {collapsed && (
                  <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-dark-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                    {item.label}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;